import React from 'react';
import { LogOut, ExternalLink } from 'lucide-react';
import customOktaAuth from '../utils/customOktaAuth';

const AccessDenied = () => {
  const handleLogout = () => {
    customOktaAuth.logout();
  };

  const serviceNowLink = 'https://dish.service-now.com/itsc?id=sc_cat_item&table=sc_cat_item&sys_id=691ee57b0f040200f563e498b1050e35&recordUrl=com.glideapp.servicecatalog_cat_item_view.do%3Fv%3D1&sysparm_id=691ee57b0f040200f563e498b1050e35';

  return (
    <div className="flex h-screen bg-dark-bg text-gray-100 relative overflow-hidden">
      {/* Neon 80s background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Grid lines effect */}
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(rgba(178, 75, 243, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(178, 75, 243, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }} />
        
        {/* Neon glows */}
        <div className="absolute top-0 left-1/3 w-[600px] h-[600px] bg-purple-accent opacity-[0.08] blur-[180px] rounded-full animate-pulse" 
             style={{ animationDuration: '6s' }} />
        <div className="absolute bottom-0 right-1/3 w-[500px] h-[500px] bg-cyan-accent opacity-[0.06] blur-[170px] rounded-full animate-pulse" 
             style={{ animationDuration: '8s', animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 w-[400px] h-[400px] bg-orange-accent opacity-[0.05] blur-[160px] rounded-full animate-pulse" 
             style={{ animationDuration: '10s', animationDelay: '2s' }} />
      </div>

      <div className="flex-1 flex items-center justify-center relative z-10 p-4 sm:p-6">
        <div className="max-w-2xl w-full bg-dark-card border border-dark-border rounded-lg p-6 sm:p-8 relative overflow-hidden"
             style={{
               background: '#141414',
               boxShadow: '0 0 60px rgba(0, 0, 0, 0.8), 0 0 2px rgba(178, 75, 243, 0.1)'
             }}>
          
          {/* Neon glow effect */}
          <div className="absolute top-20 left-1/2 w-64 h-64 bg-orange-accent opacity-[0.1] blur-[100px] rounded-full pointer-events-none animate-pulse" 
               style={{ animationDuration: '3s', transform: 'translateX(-50%)' }} />
          
          <div className="relative z-10">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-orange-accent opacity-30 blur-2xl rounded-full" />
                <div className="w-20 h-20 rounded-full bg-orange-accent/20 border-2 border-orange-accent flex items-center justify-center relative z-10"
                     style={{ 
                       boxShadow: '0 0 40px rgba(255, 107, 53, 0.5), inset 0 0 20px rgba(255, 107, 53, 0.2)'
                     }}>
                  <svg className="w-10 h-10 text-orange-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Title */}
            <h1 className="text-2xl sm:text-3xl font-bold text-center mb-4 text-orange-accent"
                style={{ textShadow: '0 0 20px rgba(255, 107, 53, 0.8), 0 0 40px rgba(255, 107, 53, 0.4)' }}>
              Access Denied
            </h1>

            {/* Message */}
            <div className="text-center mb-6 sm:mb-8 space-y-3 sm:space-y-4">
              <p className="text-base sm:text-lg text-gray-300 leading-relaxed">
                You don't have the required permissions to access this application.
              </p>
              <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
                In order to have access to the site, you'll need to request access through ServiceNow.
              </p>
            </div>

            {/* ServiceNow Link */}
            <div className="mb-6 sm:mb-8">
              <a
                href={serviceNowLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center space-x-2 sm:space-x-3 px-4 sm:px-6 py-3 sm:py-4 bg-cyan-accent/10 border-2 border-cyan-accent rounded-lg transition-all hover:bg-cyan-accent/20 hover:border-cyan-accent/80 group"
                style={{
                  boxShadow: '0 0 30px rgba(0, 229, 255, 0.3), inset 0 0 15px rgba(0, 229, 255, 0.1)'
                }}
              >
                <ExternalLink size={18} className="text-cyan-accent group-hover:scale-110 transition-transform flex-shrink-0" 
                               style={{ filter: 'drop-shadow(0 0 8px currentColor)' }} />
                <span className="text-cyan-accent font-semibold text-sm sm:text-lg"
                      style={{ textShadow: '0 0 10px currentColor' }}>
                  Request Access via ServiceNow
                </span>
              </a>
            </div>

            {/* Instructions */}
            <div className="bg-dark-border/20 border border-dark-border rounded-lg p-4 sm:p-6 mb-6 sm:mb-8">
              <h2 className="text-base sm:text-lg font-semibold text-gray-300 mb-3">Instructions:</h2>
              <ol className="list-decimal list-inside space-y-2 text-sm sm:text-base text-gray-400">
                <li>Click the "Request Access via ServiceNow" button above</li>
                <li>Select <strong className="text-cyan-accent">App-VISION-User</strong> from the available options</li>
                <li>Submit your request and wait for approval</li>
                <li>Once approved, log out and log back in to refresh your permissions</li>
              </ol>
            </div>

            {/* Logout Button */}
            <div className="flex justify-center">
              <button
                onClick={handleLogout}
                className="flex items-center space-x-3 px-6 py-3 bg-dark-border/40 border border-dark-border rounded-lg transition-all hover:bg-orange-accent/10 hover:text-orange-accent hover:border-orange-accent/40 text-gray-400 group"
              >
                <LogOut size={18} strokeWidth={2.5} className="group-hover:scale-110 transition-transform" />
                <span className="font-semibold">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessDenied;

